import { useCallback, useEffect, useRef } from 'react'

import type { WidgetState } from '../Copilot'
import { transcriptionStore } from '../sessionStore'
import { store } from '../useFeed'
import { type CopilotPersistentStateV1, serializeState } from './schema'
import { saveSessionState } from './storage'

type UseSessionPersistenceParams = {
  sessionUid: string
  queue: string
  customer: string
  callState: WidgetState['currentCallState']
  hasTranscription: boolean
  getTranscription: () => string
}

export const useSessionPersistence = ({
  sessionUid,
  queue,
  customer,
  callState,
  hasTranscription,
  getTranscription,
}: UseSessionPersistenceParams) => {
  const latestMetaRef = useRef({
    sessionUid,
    queue,
    customer,
    callState,
    hasTranscription,
    getTranscription,
  })
  const lastRunRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const pendingRef = useRef<CopilotPersistentStateV1 | null>(null)

  const persist = useCallback((state: CopilotPersistentStateV1) => {
    lastRunRef.current = Date.now()
    pendingRef.current = null
    saveSessionState(state)
  }, [])

  const flushPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }

    const pending = pendingRef.current
    if (pending) {
      persist(pending)
    }
  }, [persist])

  const buildPayload = useCallback((): CopilotPersistentStateV1 | null => {
    const meta = latestMetaRef.current
    if (!meta.sessionUid) return null

    const { feed, sessionUid: feedSessionUid } = store.state
    const feedToPersist = feedSessionUid === meta.sessionUid ? feed : []

    const stateToPersist = {
      session: {
        uid: meta.sessionUid,
        queue: meta.queue,
        customer: meta.customer,
        callState: meta.callState,
        hasTranscription: meta.hasTranscription,
        transcription: meta.getTranscription() || '',
      },
      feed: feedToPersist,
    }
    try {
      return serializeState(stateToPersist)
    } catch (error) {
      console.error('[sessionPersistence] serializeState failed', {
        sessionUid: meta.sessionUid,
        feedSessionUid,
        feedLength: feed.length,
        persistedFeedLength: feedToPersist.length,
        stateToPersist,
        error:
          error instanceof Error
            ? { name: error.name, message: error.message }
            : String(error),
      })
      return null
    }
  }, [])

  const throttledSave = useCallback(
    (payload: CopilotPersistentStateV1) => {
      const now = Date.now()
      const elapsed = now - lastRunRef.current

      if (elapsed >= 500 || lastRunRef.current === 0) {
        persist(payload)
        return
      }

      pendingRef.current = payload

      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = undefined
          const pending = pendingRef.current
          if (pending) {
            persist(pending)
          }
        }, 500 - elapsed)
      }
    },
    [persist],
  )

  const schedulePersist = useCallback(
    (source: 'meta-change' | 'feed-change' | 'transcription-change') => {
      const payload = buildPayload()
      if (!payload) return

      console.debug('[sessionPersistence] schedulePersist', {
        source,
        sessionUid: payload.session.uid,
        feedLength: payload.feed.length,
        callState: payload.session.callState,
        savedAt: payload.savedAt,
      })

      throttledSave(payload)
    },
    [buildPayload, throttledSave],
  )

  useEffect(() => {
    latestMetaRef.current = {
      sessionUid,
      queue,
      customer,
      callState,
      hasTranscription,
      getTranscription,
    }

    if (!sessionUid) return

    schedulePersist('meta-change')
  }, [
    sessionUid,
    queue,
    customer,
    callState,
    hasTranscription,
    getTranscription,
    schedulePersist,
  ])

  useEffect(() => {
    if (!sessionUid) return

    // Subscribe to feed changes
    const unsubscribe = store.subscribe(({ currentVal }) => {
      if (currentVal.sessionUid !== sessionUid) return
      schedulePersist('feed-change')
    })
    const unsubscribeTranscription = transcriptionStore.subscribe(() =>
      schedulePersist('transcription-change'),
    )

    return () => {
      unsubscribe()
      unsubscribeTranscription()
      flushPending()
    }
  }, [sessionUid, schedulePersist, flushPending])
}
